import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "../constants/apiClient";
import { toast } from "../utils/toast";

export interface RegisterConfig {
    id: number;
    client: string;
    room: string;
    ip: string;
    port: number;
    read_write_mode: string;
    key: string;
    label: string;
    description: string | null;
    register: string;
    type: string;
    model: string | null;
    factor: number;
    min_value: number | null;
    max_value: number | null;
    active: boolean;
    created: string;
}

export interface RegisterConfigCreate {
    room: string;
    ip: string;
    port: number;
    read_write_mode: string;
    key: string;
    label: string;
    description?: string;
    register: string;
    type: string;
    model?: string;
    factor?: number;
    min_value?: number | null;
    max_value?: number | null;
}

export interface RegisterConfigListResponse {
    results: RegisterConfig[];
    count: number;
}

/**
 * Combined hook for register configurations - fetching and mutations
 */
export function useDeviceRegisters(clientId: string | undefined, activeOnly = true) {
    const queryClient = useQueryClient();
    const deviceRegistersBaseEndpoint = `/device/registers/${clientId}`;

    const { data, isLoading, error, isError } = useQuery<RegisterConfigListResponse>({
        queryKey: ["deviceRegisters", clientId, activeOnly],
        queryFn: async () => {
            if (!clientId) return { results: [], count: 0 };

            const response = await apiClient.get(`${deviceRegistersBaseEndpoint}`, {
                params: { active_only: activeOnly }
            });
            return response.data;
        },
        enabled: !!clientId,
    });

    const createMutation = useMutation({
        mutationFn: async (data: RegisterConfigCreate) => {
            if (!clientId) throw new Error("Client ID is required");
            const response = await apiClient.post(`${deviceRegistersBaseEndpoint}`, data);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["deviceRegisters", clientId], exact: false });
            toast.success("Register configuration created");
        },
        onError: (error: Error) => {
            console.error("Failed to create register:", error); // eslint-disable-line no-console
        }
    });

    const updateMutation = useMutation({
        mutationFn: async ({ registerId, data }: { registerId: number; data: RegisterConfigCreate }) => {
            if (!clientId) throw new Error("Client ID is required");
            const response = await apiClient.put(`${deviceRegistersBaseEndpoint}/${registerId}`, data);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["deviceRegisters", clientId], exact: false });
            toast.success("Register configuration updated");
        },
        onError: (error: Error) => {
            console.error("Failed to update register:", error); // eslint-disable-line no-console
        }
    });

    const deleteMutation = useMutation({
        mutationFn: async (registerId: number) => {
            if (!clientId) throw new Error("Client ID is required");
            const response = await apiClient.delete(`${deviceRegistersBaseEndpoint}/${registerId}`);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["deviceRegisters", clientId], exact: false });
            toast.success("Register configuration deleted");
        },
        onError: (error: Error) => {
            console.error("Failed to delete register:", error); // eslint-disable-line no-console
        }
    });

    const syncMutation = useMutation({
        mutationFn: async () => {
            if (!clientId) throw new Error("Client ID is required");
            const response = await apiClient.post(`${deviceRegistersBaseEndpoint}/sync`);
            return response.data;
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ["syncStatus", clientId], exact: false });
            toast.success(`Configuration sent to gateway: ${data.count} registers`);
        },
        onError: (error: Error) => {
            console.error("Failed to sync configuration:", error); // eslint-disable-line no-console
            toast.error("Failed to sync configuration to gateway");
        }
    });

    return {
        data,
        isLoading,
        error,
        isError,
        createMutation,
        updateMutation,
        deleteMutation,
        syncMutation,
        deviceRegistersBaseEndpoint,
    };
}

/**
 * Hook to get sync status for a gateway
 */
export function useSyncStatus(clientId: string | undefined) {
    return useQuery({
        queryKey: ["syncStatus", clientId],
        queryFn: async () => {
            if (!clientId) return null;
            const response = await apiClient.get(`/device/registers/${clientId}/sync/status`);
            return response.data;
        },
        enabled: !!clientId,
        refetchInterval: 5000, // Poll every 5 seconds to check for sync updates
    });
}
